import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Navigate } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { Input } from "@/shared/components/Input";
import { Button } from "@/shared/components/Button";
import { authService } from "@/core/services/authService";
import { useAuth } from "@/core/hooks/useAuth";

const schema = z.object({
  codigo: z
    .string()
    .min(1, "Informe o código.")
    .regex(/^\d{6}$/, "O código tem 6 números."),
});
type FormData = z.infer<typeof schema>;

type Estado =
  | { tipo: "carregando" }
  | { tipo: "erro"; mensagem: string }
  | { tipo: "cadastro"; factorId: string; qrCodeSvg: string; secret: string }
  | { tipo: "confirmar"; factorId: string }
  | { tipo: "ok" };

/**
 * Tela de autoatendimento para verificação em duas etapas (MFA/TOTP).
 *
 * Motivação original (29/07/2026): destravar o Administrador Fantasma —
 * `eh_admin_fantasma()` só retorna true com sessão aal2 (F6-XX regra 1/16,
 * fail-closed por desenho), e não existe forma de fazer esse enrollment por
 * SQL. Decisão do Elos: deixar a tela aberta pra QUALQUER pessoa autenticada
 * configurar a própria verificação em duas etapas, não só as 2 contas
 * Fantasma — pensando em reuso futuro pra usuário comum.
 *
 * Não está linkada em nenhum menu (a invisibilidade do Fantasma, F6-XX regra
 * 2, segue pendente de decisão de produto) — só quem recebe a URL
 * `/conta/seguranca` diretamente chega aqui. A API de MFA do Supabase só
 * opera sobre a PRÓPRIA sessão logada, então isso não abre nenhuma via de
 * mexer no fator de outra pessoa.
 *
 * "Configurar MFA" cobre dois momentos diferentes, e esta tela decide sozinha
 * qual mostrar: (1) cadastro do fator, uma vez só (mostra QR code) e
 * (2) confirmação do código a cada sessão nova — toda sessão nasce aal1;
 * sem confirmar de novo aqui, o bypass do Fantasma nunca ativa de verdade,
 * mesmo com o fator já cadastrado antes.
 */
export function SegurancaConta() {
  const { pessoa, carregando } = useAuth();
  const [estado, setEstado] = useState<Estado>({ tipo: "carregando" });
  const [erroFormulario, setErroFormulario] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({ resolver: zodResolver(schema) });

  const ehFantasma = pessoa?.eh_admin_fantasma === true;

  useEffect(() => {
    // Só o Administrador Fantasma usa esta tela por enquanto; para as demais
    // pessoas nada é consultado nem cadastrado.
    if (ehFantasma) carregarEstadoInicial();
  }, [ehFantasma]);

  async function carregarEstadoInicial() {
    setEstado({ tipo: "carregando" });
    try {
      const fatores = await authService.listarFatoresMfa();
      const fatorConfirmado = fatores.find((fator) => fator.status === "verified");

      if (!fatorConfirmado) {
        const cadastro = await authService.cadastrarFatorMfa();
        setEstado({ tipo: "cadastro", ...cadastro });
        return;
      }

      const nivel = await authService.nivelSessaoMfa();
      if (nivel.currentLevel === "aal2") {
        setEstado({ tipo: "ok" });
      } else {
        setEstado({ tipo: "confirmar", factorId: fatorConfirmado.id });
      }
    } catch (e) {
      setEstado({
        tipo: "erro",
        mensagem: e instanceof Error ? e.message : "Não foi possível carregar esta página.",
      });
    }
  }

  async function onSubmit(dados: FormData) {
    if (estado.tipo !== "cadastro" && estado.tipo !== "confirmar") return;
    setErroFormulario(null);
    try {
      await authService.confirmarCodigoMfa(estado.factorId, dados.codigo);
      setEstado({ tipo: "ok" });
      reset();
    } catch (e) {
      setErroFormulario(e instanceof Error ? e.message : "Código inválido.");
    }
  }

  if (carregando) {
    return (
      <main className="mx-auto flex w-full max-w-md flex-col items-center px-4 pb-16 pt-16 text-center sm:px-6">
        <p className="text-sm text-muted">Carregando...</p>
      </main>
    );
  }

  if (!ehFantasma) {
    return <Navigate to="/inicio" replace />;
  }

  if (estado.tipo === "carregando") {
    return (
      <main className="mx-auto flex w-full max-w-md flex-col items-center px-4 pb-16 pt-16 text-center sm:px-6">
        <p className="text-sm text-muted">Carregando...</p>
      </main>
    );
  }

  if (estado.tipo === "erro") {
    return (
      <main className="mx-auto flex w-full max-w-md flex-col items-center gap-3 px-4 pb-16 pt-16 text-center sm:px-6">
        <p className="text-sm text-muted">{estado.mensagem}</p>
        <Button type="button" onClick={carregarEstadoInicial} className="w-auto px-6">
          Tentar novamente
        </Button>
      </main>
    );
  }

  if (estado.tipo === "ok") {
    return (
      <main className="mx-auto w-full max-w-md px-4 pb-16 pt-10 sm:px-6">
        <div className="rounded-2xl border border-success/30 bg-success/5 p-6 text-center">
          <div className="mx-auto mb-3 grid h-11 w-11 place-items-center rounded-full bg-success text-on-primary">
            <ShieldCheck size={22} />
          </div>
          <p className="text-sm font-semibold text-text">Verificação confirmada!</p>
          <p className="mt-1 text-xs text-muted">
            A verificação em duas etapas está ativa nesta sessão.
          </p>
        </div>
      </main>
    );
  }

  const ehCadastro = estado.tipo === "cadastro";

  return (
    <main className="mx-auto w-full max-w-md px-4 pb-16 pt-10 sm:px-6">
      <header className="mb-6 text-center">
        <h1 className="text-xl font-bold text-text">
          {ehCadastro ? "Configurar verificação em duas etapas" : "Confirme seu código"}
        </h1>
        <p className="mt-1 text-sm text-muted">
          {ehCadastro
            ? "Escaneie o QR code com um app autenticador (Google Authenticator, Authy, etc.) e confirme com o código gerado."
            : "Sua conta já tem verificação em duas etapas configurada — confirme o código para ativar nesta sessão."}
        </p>
      </header>

      {ehCadastro && (
        <>
          <div className="mb-4 flex justify-center">
            {/*
              `totp.qr_code` (SDK @supabase/auth-js) já vem como uma data: URI
              COMPLETA e pronta pra uso — confirmado inspecionando o valor
              real vindo do DSV (começa com "data:image/svg+xml;utf-8,<?xml
              ..."), não é só o markup SVG. Por isso vai direto no `src`, sem
              nenhum prefixo/encoding adicional — duas tentativas anteriores
              (dangerouslySetInnerHTML, depois prefixar+encodar de novo)
              geravam URI inválida/duplamente aninhada e quebravam a leitura
              (achado relatado pelo Elos em 29/07/2026, confirmado com
              screenshot renderizado num navegador real antes deste fix).
            */}
            <div className="rounded-xl border border-border bg-surface-2 p-3">
              <img
                src={estado.qrCodeSvg}
                alt="QR code para configurar o app autenticador"
                className="h-40 w-40"
              />
            </div>
          </div>
          <div className="mb-6 rounded-md bg-surface-2 px-4 py-3 text-center text-xs text-muted">
            Ou digite este código manualmente no app
            <div className="mt-1 break-all font-mono text-sm font-semibold tracking-wide text-text">
              {estado.secret}
            </div>
          </div>
        </>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4" noValidate>
        <Input
          label="Código de 6 dígitos"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          placeholder="000000"
          className="text-center text-lg tracking-[0.4em]"
          erro={errors.codigo?.message}
          {...register("codigo")}
          onChange={(e) => {
            e.target.value = e.target.value.replace(/\D/g, "").slice(0, 6);
          }}
        />

        {erroFormulario && (
          <p role="alert" className="rounded-md bg-danger/10 px-3 py-2 text-xs text-danger">
            {erroFormulario}
          </p>
        )}

        <Button type="submit" carregando={isSubmitting}>
          {ehCadastro ? "Confirmar e ativar" : "Confirmar"}
        </Button>
      </form>
    </main>
  );
}
